<script setup>
import { computed, ref } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import {
  useVueTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  createColumnHelper,
} from '@tanstack/vue-table';
import Table from 'dashboard/components/table/Table.vue';
import Pagination from 'dashboard/components/table/Pagination.vue';
import EmptyState from 'dashboard/components/widgets/EmptyState.vue';
import Spinner from 'shared/components/Spinner.vue';
import { onMounted } from 'vue';
import { campaignStatusLabel } from 'dashboard/routes/dashboard/campaigns/helper/campaignStatus';

// Propriedades recebidas
const props = defineProps({
  campaignId: {
    type: [String, Number],
    required: true,
  },
});

// Inicialização de dependências
const { t } = useI18n();
const store = useStore();
const columnHelper = createColumnHelper();

// Estado reativo
const sorting = ref([]);
const pagination = ref({
  pageIndex: 0,
  pageSize: 10,
});

// Estado de carregamento
const uiFlags = computed(() => {
  return store.getters['campaigns/getUIFlags'] || { isFetching: false };
});

// Computa os dados de schedulings
const schedulingData = computed(() => {
  const campaigns =
    store.getters['campaigns/getAllCampaignsWhatsappApiOficial'] || [];
  const campaign = campaigns.find(c => c.id === Number(props.campaignId));
  const schedulings = campaign?.campaign_schedules || [];
  return Array.isArray(schedulings) ? schedulings : [];
});

// Definição das colunas da tabela
const columns = [
  columnHelper.accessor('scheduled_at', {
    header: t('Data Agendada'),
    cell: info => {
      const value = info.getValue();
      return typeof value === 'number'
        ? new Date(value * 1000).toLocaleString('pt-BR')
        : new Date(value).toLocaleString('pt-BR');
    },
    size: 200,
    enableSorting: true,
  }),
  columnHelper.accessor('campaign_status', {
    header: t('Status'),
    // campaign_status é enum CRU: usa o label do payload ou traduz via i18n
    cell: info =>
      info.row.original.campaign_status_label ||
      campaignStatusLabel(info.getValue(), t),
    enableSorting: true,
  }),
  columnHelper.accessor('filter_data', {
    header: t('Tabela'),
    cell: info => info.getValue()?.tabela,
    enableSorting: true,
  }),
  columnHelper.accessor('filter_data', {
    header: t('Filtros'),
    cell: info => {
      const filtros = info.getValue()?.filtros;
      try {
        const parsed =
          typeof filtros === 'string' ? JSON.parse(filtros) : filtros;
        if (!Array.isArray(parsed)) return '-';

        return parsed
          .map(f => {
            const operador =
              f.filter_operator === 'equal_to' ? 'igual a' : f.filter_operator;
            const atributo =
              f.attribute_key === 'filial' ? 'Filial' : f.attribute_key;
            return `${operador} ${atributo} ${f.values}`;
          })
          .join(', ');
      } catch (e) {
        return '-';
      }
    },
    enableSorting: true,
  }),

  columnHelper.accessor('filter_data', {
    header: t('Placeholders'),
    cell: info => {
      const placeholders = info.getValue()?.placeholders;

      if (!placeholders || typeof placeholders !== 'object') return '-';

      return Object.entries(placeholders)
        .map(([key, value]) => `${key} → ${value}`)
        .join(', ');
    },
    enableSorting: true,
  }),

  columnHelper.accessor('filter_data', {
    header: t('Coluna Telefone'),
    cell: info => info.getValue()?.send_message?.phone_column,
    enableSorting: true,
  }),

  {
    id: 'actions',
    header: 'Ações',
    enableSorting: false,
  },
];

// Configuração da tabela
const table = useVueTable({
  get data() {
    return schedulingData.value;
  },
  columns,
  state: {
    get sorting() {
      return sorting.value;
    },
    get pagination() {
      return pagination.value;
    },
  },
  onSortingChange: updater => {
    sorting.value =
      typeof updater === 'function' ? updater(sorting.value) : updater;
    if (!Array.isArray(sorting.value)) {
      sorting.value = [];
    }
  },
  onPaginationChange: updater => {
    pagination.value =
      typeof updater === 'function' ? updater(pagination.value) : updater;
  },
  getCoreRowModel: getCoreRowModel(),
  getSortedRowModel: getSortedRowModel(),
  getPaginationRowModel: getPaginationRowModel(),
});

const loadCampaigns = async () => {
  if (!store.getters['campaigns/getAllCampaignsWhatsappApiOficial']?.length) {
    await store.dispatch('campaigns/get');
  }
};

onMounted(() => {
  loadCampaigns();
});
</script>

<template>
  <div class="whatsapp-schedule-list-page">
    <!-- Cabeçalho da página -->
    <header class="page-header">
      <div class="action-bar__guideline">
        <h2 class="header">
          {{ $t('CAMPAIGN.WHATSAPP_OFFICIAL.SCHEDULE.LIST.HEADER_TITLE') }}
        </h2>
        <p class="guideline-text">
          {{ $t('CAMPAIGN.WHATSAPP_OFFICIAL.SCHEDULE.LIST.GUIDELINE_TEXT') }}
        </p>
      </div>
    </header>

    <!-- Corpo da página -->
    <main class="page-body">
      <div v-if="uiFlags.isFetching" class="loading-container">
        <Spinner />
        <span>{{
          $t('CAMPAIGN.WHATSAPP_OFFICIAL.SCHEDULE.TABLE.LOADING_MESSAGE')
        }}</span>
      </div>
      <div v-else-if="!schedulingData.length" class="loading-container">
        <EmptyState
          :title="$t('CAMPAIGN.WHATSAPP_OFFICIAL.SCHEDULE.LIST.NO_SCHEDULES')"
        />
      </div>
      <div v-else>
        <Table :table="table" type="fixed" />
        <Pagination
          :table="table"
          :default-page-size="10"
          show-page-size-selector
          class="pagination"
        />
      </div>
    </main>
  </div>
</template>

<style scoped>
.whatsapp-schedule-list-page {
  @apply flex flex-col min-h-screen bg-slate-25 dark:bg-slate-900;
}

.page-header {
  @apply bg-green-300 text-white p-6 min-h-[150px] flex items-center dark:bg-green-900;
}

.action-bar__guideline {
  @apply flex flex-col gap-2;
}

.header {
  @apply m-0 text-2xl font-semibold;
}

.guideline-text {
  @apply m-0 text-base text-slate-50 max-w-[600px];
}

.page-body {
  @apply flex-1 p-0;
}

.loading-container {
  @apply flex items-center justify-center p-8 text-base;
}

.actions-column {
  @apply flex gap-2 items-center;
}

.pagination {
  @apply mr-1 ml-1 p-2;
}

:deep(.table-container) {
  @apply border border-slate-100 overflow-hidden bg-white dark:bg-slate-800 dark:border-slate-700;
}

:deep(th) {
  @apply bg-slate-25 font-semibold text-left p-3 dark:bg-slate-700 dark:text-slate-100;
}

:deep(td) {
  @apply p-3 border-t border-slate-100 dark:border-slate-700 dark:text-slate-100;
}

:deep(tr:last-child td) {
  @apply border-b-0;
}
</style>
